import React from "react";
import { useChannelStateContext, useChatContext } from "stream-chat-react";
import { Check, EllipsisVertical, Star } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import ChannelUserAvatar from "@/components/pages/chats/component/chat-custom-components/ChannelUserAvatar";
import { useReservation } from "@/hooks/useReservations";
import { Reservation } from "@/types/reservation";
import { getRoomShortCode } from "@/components/pages/chats/utils/roomShortCode";
import { useChannelPin } from "@/components/pages/chats/hooks/useChannelPin";
import {
  ChatProfileSection,
  useChatProfileSections,
} from "@/contexts/ChatProfileSectionsContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { PinnedMessagesDialog } from "./PinnedMessagesDialog";

const PROFILE_SECTIONS: { section: ChatProfileSection; labelKey: string }[] = [
  { section: "reservations", labelKey: "chats.reservationsSection" },
  { section: "rooms", labelKey: "chats.roomsSection" },
];

interface HeaderActionProps {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}

const HeaderAction: React.FC<HeaderActionProps> = ({
  label,
  onClick,
  disabled,
  className,
  children,
}) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    onClick={onClick}
    disabled={disabled}
    className={cn(
      "flex size-9 items-center justify-center rounded-lg text-zinc-600 transition-colors hover:bg-neutral-100 hover:text-zinc-900 disabled:opacity-50",
      className,
    )}
  >
    {children}
  </button>
);

export const GuestChannelHeader = () => {
  const { t } = useTranslation();
  const { client } = useChatContext();
  const { channel } = useChannelStateContext();
  const { isPinned, isPinning, togglePin } = useChannelPin(channel);
  const { visibility, toggleSection } = useChatProfileSections();

  const currentUserId = client.userID;

  const otherMember = Object.values(channel.state.members).find(
    (member) => member.user?.id !== currentUserId,
  )?.user;

  const guestName =
    otherMember?.name || channel.data?.name || channel.data?.created_by?.name;

  const bookingId =
    channel?.data?.bookingId || channel?.data?.metadata?.bookingId;

  const { data: channelRelatedBooking } =
    useReservation<Reservation>(bookingId);

  const bookingRoom = channelRelatedBooking?.room;
  const pmsCategoryName = bookingRoom?.category?.pmsType?.trim();
  const categoryName = bookingRoom?.category?.name?.trim();
  const roomCategoryLabel = pmsCategoryName || categoryName;
  const roomShortCode = getRoomShortCode(roomCategoryLabel);

  const handleTogglePin = async () => {
    try {
      await togglePin();
    } catch (error) {
      console.error("Failed to toggle channel pin:", error);
      toast.error(isPinned ? t("chats.unpinError") : t("chats.pinError"));
    }
  };

  return (
    <div className="flex h-[62px] shrink-0 items-center justify-between gap-4 border-b border-neutral-200 bg-white px-4">
      <div className="flex min-w-0 items-center gap-4">
        <div className="relative shrink-0">
          <ChannelUserAvatar name={guestName} className="size-9" />
          {bookingRoom?.number != null ? (
            <span className="bg-monochrome-core-2 absolute -bottom-1 left-1/2 flex h-4 min-w-5 -translate-x-1/2 items-center justify-center rounded-lg px-1 text-[10px] leading-none font-semibold text-white">
              {bookingRoom.number}
            </span>
          ) : null}
        </div>

        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-eathy-comfort-1 truncate text-base font-semibold">
            {guestName}
          </span>
          {(roomShortCode || roomCategoryLabel) && (
            <div className="flex min-w-0 items-center gap-1 text-xs">
              {roomShortCode && (
                <span className="text-light-olive/75 shrink-0 font-medium">
                  {roomShortCode}
                </span>
              )}
              {roomCategoryLabel && (
                <span className="text-eathy-comfort-1/50 truncate">
                  {roomCategoryLabel}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className={cn("flex shrink-0 items-center gap-1")}>
        <PinnedMessagesDialog />
        <HeaderAction
          label={isPinned ? t("chats.unpinChat") : t("chats.pinChat")}
          onClick={handleTogglePin}
          disabled={isPinning}
          className={cn(
            isPinned && "text-fresh-orange hover:text-fresh-orange",
          )}
        >
          <Star className={cn("size-4", isPinned && "fill-current")} />
        </HeaderAction>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t("chats.moreActions")}
              title={t("chats.moreActions")}
              className="flex size-9 items-center justify-center rounded-lg text-zinc-600 transition-colors hover:bg-neutral-100 hover:text-zinc-900"
            >
              <EllipsisVertical className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52 py-2">
            {PROFILE_SECTIONS.map(({ section, labelKey }) => (
              <DropdownMenuItem
                key={section}
                role="menuitemcheckbox"
                aria-checked={visibility[section]}
                onSelect={(event) => {
                  event.preventDefault();
                  toggleSection(section);
                }}
                className="py-2.5"
              >
                <span>{t(labelKey)}</span>
                {visibility[section] && (
                  <Check className="ml-auto size-4" aria-hidden />
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
